import { WidgetProps } from '../../../../types/WidgetProps';
import { GooglePayOptionsStore } from '../../../../types/StoreState';
import { GooglePayPaymentMethod } from '../../../../types/PaymentMethods';
import { getShippingPaymentRequestData } from './getShippingPaymentRequestData';
import { getTransactionInfo } from './getTransactionInfo';
import { getCallbackIntents } from './getCallbackIntents';
import { getMerchantInfo } from './getMerchantInfo';

interface GetPaymentDataRequestParams {
  topProps: WidgetProps;
  countryCode: string;
  sessionId: string;
  googlePayPM: GooglePayPaymentMethod;
  googlePayOptions: GooglePayOptionsStore;
  shippingMethods: WidgetProps['shippingMethods'];
}

/**
 * @link https://developers.google.com/pay/api/web/reference/request-objects#PaymentDataRequest
 */
export const getPaymentDataRequest = ({
  topProps,
  countryCode,
  sessionId,
  googlePayPM,
  googlePayOptions,
  shippingMethods,
}: GetPaymentDataRequestParams): google.payments.api.PaymentDataRequest => {
  const merchantInfo = getMerchantInfo(googlePayOptions.data.merchantInfo);
  const transactionInfo = getTransactionInfo({
    currency: topProps.currency,
    paymentAmount: topProps.paymentAmount,
    paymentBreakdown: topProps.paymentBreakdown,
    countryCode,
    sessionId,
    requestShipping: topProps.requestShipping,
  });
  const shippingPaymentRequestData = getShippingPaymentRequestData({
    requestShipping: topProps.requestShipping,
    locale: topProps.locale,
    currency: topProps.currency,
    shippingMethods,
  });
  const callbackIntents = getCallbackIntents({
    shippingOptionRequired: shippingPaymentRequestData.shippingOptionRequired,
    shippingAddressRequired: shippingPaymentRequestData.shippingAddressRequired,
  });

  return {
    callbackIntents,
    apiVersion: 2,
    apiVersionMinor: 0,
    emailRequired: true,
    allowedPaymentMethods: [
      {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: googlePayPM.googlePay.allowedAuthMethods,
          allowedCardNetworks: googlePayPM.googlePay.allowCardNetworks,
          billingAddressRequired: true,
          billingAddressParameters: {
            format: 'FULL',
            phoneNumberRequired: true,
          },
        },
        tokenizationSpecification: googlePayOptions.data.tokenizationSpecification,
      },
    ],
    merchantInfo,
    ...shippingPaymentRequestData,
    transactionInfo,
  };
};
